/**
 * AccessRequirements.tsx — PlatformOwner-only editing of `app.access_requirement` (CAT-F13, CAT-F14,
 * CAT-NFR04): the vocabulary a builder picks from when deciding who may see the tool they are sharing.
 * Built on the Kombi design system (kombi/admin/chrome) and mounted as an in-place modal panel over AdminHome
 * (AccessRequirementsPanel), rather than the old full-page navy screen. The still-registered
 * /admin/access-requirements route renders through a thin AccessRequirements() wrapper that opens the
 * same panel over a blank backdrop for the deep-link path. The panel fetches its own data on open via
 * useOverlayData (AdminHome does not preload it) and, after every mutation, calls reload() to refresh
 * in place instead of a router revalidation.
 *
 * WHY THIS SCREEN IS DIFFERENT FROM THE OTHER ADMIN PANELS. Every other value list here is a label.
 * This one is an ACCESS CONTROL: changing a row's rule changes who can see every tool already
 * carrying it, immediately, in both directions, and no tool record changes — so nothing on a tool's
 * own page will ever explain why it vanished. That single fact drives every layout decision below,
 * and none of it changes just because the screen now lives inside the overlay frame.
 *
 *   - THE CONSEQUENCE IS IN THE LIST, NOT IN A DIALOG. Each row states how many tools carry it before
 *     the owner clicks anything, because a number that appears only after a decision has been made is
 *     a number that arrives too late to change it. Same reasoning as the departure-candidates list.
 *
 *   - `rule_value` IS A PICKER, NEVER A TEXT BOX. A free-text box's most likely output is a typo, and
 *     a typo'd rule does not error: it produces a requirement nobody satisfies, so the tools carrying
 *     it silently stop being findable by anyone but their builders and the owner. The options come
 *     from the same GET that returns the rows, so the picker cannot offer a value the server refuses.
 *
 *   - EACH DEPARTMENT OPTION SHOWS ITS MEMBER COUNT, and a zero is shown rather than hidden. Zero is
 *     legitimate — a restriction can be prepared before its members sign in — but it is also what a
 *     mistake looks like, so it belongs in front of the owner while they are still choosing.
 *
 *   - RETIRE IS NOT DELETE, AND THE BUTTON SAYS SO. Tools keep a retired requirement and stay exactly
 *     as visible as before; retiring withdraws the CHOICE from future uploads. There is no delete
 *     control because there is no delete route.
 *
 * The rule editor is the only control that asks for a typed confirmation, and only when tools carry
 * the row (PC-F18). The server refuses without it (`consequence_not_acknowledged`), so the checkbox is
 * not the enforcement — it is the sentence the owner has to read.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  apiGet,
  apiPatch,
  apiPost,
  type AccessRequirementRow,
  type AccessRequirementsPayload,
  type AccessRuleKind,
  type ApiResult,
  type DepartmentOption,
} from './api';
import { shortWhen } from './shared';
import {
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  QuietAction,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type AccessRequirementsLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | { state: 'ok'; payload: AccessRequirementsPayload };

export async function accessRequirementsLoader(): Promise<AccessRequirementsLoaderData> {
  const res = await apiGet<AccessRequirementsPayload>('/api/admin/access-requirements');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/access-requirements failed (${res.status})`);
  }
  return { state: 'ok', payload: res.body };
}

/**
 * The refusal codes turned into sentences that say what to do next. The server's `error` string is the
 * contract; leaving it on screen raw ("restricting_cannot_be_default") tells an owner that something
 * failed without telling them why it would ever be refused — which is the whole point of these guards.
 */
const REFUSAL_TEXT: Record<string, string> = {
  invalid_label: 'A name needs between 3 and 120 characters.',
  duplicate_label: 'Another value already uses that name.',
  rule_value_required: 'Choose which department or role this restricts to.',
  unknown_department:
    'Nobody records that department, and it is not on the governed list. Pick one of the offered options.',
  unknown_appointment_role: 'That is not one of the platform roles.',
  already_retired: 'It is already retired.',
  not_retired: 'It is not retired.',
  already_default: 'It is already the default.',
  default_cannot_be_retired:
    'The default cannot be retired — the upload form pre-selects it. Make another value the default first, then retire this one.',
  retired_cannot_be_default: 'Reinstate it before making it the default.',
  restricting_cannot_be_default:
    'The default is what a builder gets when they express no preference, so it must not restrict — otherwise every future upload is silently limited to one department or role.',
  consequence_not_acknowledged: 'Tick the confirmation to say you have read the consequence.',
  forbidden: 'Only the platform owner can change the access list.',
};

function refusalText(res: ApiResult<unknown>): string {
  if (res.kind === 'unauthorized') return 'Your session has expired — sign in again.';
  if (res.kind !== 'error') return 'Saved.';
  const code = res.code ?? '';
  return REFUSAL_TEXT[code] ?? `Failed (${res.status}${code ? `: ${code}` : ''}).`;
}

/**
 * The success confirmations `run`/`submit` set on an ok mutation. Every other `message` value is a
 * refusal sentence from `refusalText`, so anything NOT in this set reads like an error and is shown
 * as one — the presentation split is cosmetic; the message strings themselves are unchanged.
 */
const DONE_MESSAGES = new Set([
  'Added.',
  'Renamed.',
  'Rule changed.',
  'Retired.',
  'Reinstated.',
  'Default moved.',
]);

/** True when a per-row status message reads like a failure rather than a success confirmation. */
function isRefusal(message: string): boolean {
  return !DONE_MESSAGES.has(message);
}

/** What a row's rule admits, in words rather than in column values. */
function ruleSentence(row: AccessRequirementRow): string {
  if (row.ruleKind === 'department') return `Only the ${row.ruleValue ?? '—'} department`;
  if (row.ruleKind === 'appointment') return `Only people holding ${row.ruleValue ?? '—'}`;
  return 'Everyone at the company';
}

/**
 * The kind + value pair, shared by the create form and the per-row rule editor so both offer exactly
 * the same options. `value` is meaningless for 'all' and the control disappears rather than being
 * disabled: a greyed-out picker invites the reading that the value is being kept somewhere.
 */
function RuleFields({
  kind,
  value,
  departments,
  appointmentRoles,
  onKind,
  onValue,
  idPrefix,
}: {
  kind: AccessRuleKind;
  value: string;
  departments: DepartmentOption[];
  appointmentRoles: string[];
  onKind: (k: AccessRuleKind) => void;
  onValue: (v: string) => void;
  idPrefix: string;
}) {
  return (
    <>
      <label className="kombi-field" htmlFor={`${idPrefix}-kind`}>
        <span>Who it admits</span>
        <select
          id={`${idPrefix}-kind`}
          value={kind}
          onChange={(e) => {
            const next = e.target.value as AccessRuleKind;
            onKind(next);
            // Clearing on every switch, so a value chosen for 'department' cannot be submitted as an
            // 'appointment' role and refused for a reason the owner did not cause.
            onValue('');
          }}
        >
          <option value="all">Everyone at the company</option>
          <option value="department">One department only</option>
          <option value="appointment">One platform role only</option>
        </select>
      </label>
      {kind === 'department' && (
        <label className="kombi-field" htmlFor={`${idPrefix}-dept`}>
          <span>Department</span>
          <select id={`${idPrefix}-dept`} value={value} onChange={(e) => onValue(e.target.value)}>
            <option value="">Choose…</option>
            {departments.map((d) => (
              <option key={d.label} value={d.label}>
                {d.label} — {d.memberCount} {d.memberCount === 1 ? 'person' : 'people'}
                {d.memberCount === 0 ? ' (nobody yet)' : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {kind === 'appointment' && (
        <label className="kombi-field" htmlFor={`${idPrefix}-role`}>
          <span>Role</span>
          <select id={`${idPrefix}-role`} value={value} onChange={(e) => onValue(e.target.value)}>
            <option value="">Choose…</option>
            {appointmentRoles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  );
}

function CreateForm({
  departments,
  appointmentRoles,
  onSaved,
}: {
  departments: DepartmentOption[];
  appointmentRoles: string[];
  onSaved: () => void;
}) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<AccessRuleKind>('all');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiPost<{ ok: true }>('/api/admin/access-requirements', {
        label,
        ruleKind: kind,
        ...(kind === 'all' ? {} : { ruleValue: value }),
      });
      if (res.kind === 'ok') {
        setLabel('');
        setKind('all');
        setValue('');
        setMessage('Added.');
        onSaved();
        return;
      }
      setMessage(refusalText(res));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Notice>
        Added as an option only — neither the default nor applied to anything. Builders can choose
        it on their next upload, and existing tools are untouched.
      </Notice>
      <div className="kombi-dialog-stack">
        <label className="kombi-field">
          <span>Name</span>
          <input
            id="ar-new-label"
            onChange={(event) => setLabel(event.target.value)}
            placeholder="e.g. Finance only"
            type="text"
            value={label}
          />
        </label>
        <RuleFields
          kind={kind}
          value={value}
          departments={departments}
          appointmentRoles={appointmentRoles}
          onKind={setKind}
          onValue={setValue}
          idPrefix="ar-new"
        />
      </div>
      <div className="kombi-admin-row-actions">
        <PrimaryAction
          disabled={busy || label.trim().length < 3 || (kind !== 'all' && value === '')}
          onClick={() => void submit()}
        >
          {busy ? 'Adding…' : 'Add value'}
        </PrimaryAction>
        {message && <span className="learn-note"> {message}</span>}
      </div>
    </div>
  );
}

function RequirementRow({
  row,
  departments,
  appointmentRoles,
  onSaved,
}: {
  row: AccessRequirementRow;
  departments: DepartmentOption[];
  appointmentRoles: string[];
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(row.label);
  const [kind, setKind] = useState<AccessRuleKind>(row.ruleKind ?? 'all');
  const [value, setValue] = useState(row.ruleValue ?? '');
  const [acknowledged, setAcknowledged] = useState(false);
  const [editingRule, setEditingRule] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const base = `/api/admin/access-requirements/${row.accessRequirementId}`;

  async function run(call: () => Promise<ApiResult<unknown>>, done: string) {
    setBusy(true);
    setMessage(null);
    try {
      const res = await call();
      setMessage(res.kind === 'ok' ? done : refusalText(res));
      if (res.kind === 'ok') {
        setEditingRule(false);
        setAcknowledged(false);
        onSaved();
      }
    } finally {
      setBusy(false);
    }
  }

  const carriesTools = row.toolCount > 0;

  return (
    <li className="kombi-admin-card">
      <div>
        {row.label}
        {row.isDefault && ' · default'}
        {row.retiredAt !== null && ' · retired'}
      </div>
      <p className="learn-note">
        {ruleSentence(row)} ·{' '}
        {/* The consequence figure, stated before any action rather than inside a confirmation. */}
        {row.toolCount} {row.toolCount === 1 ? 'tool carries' : 'tools carry'} this
        {row.retiredAt !== null && ` · retired ${shortWhen(row.retiredAt)}`}
      </p>

      <div className="kombi-dialog-stack">
        <label className="kombi-field">
          <span>Name</span>
          <input
            id={`ar-${row.accessRequirementId}-label`}
            onChange={(event) => setLabel(event.target.value)}
            type="text"
            value={label}
          />
        </label>
        <div className="kombi-admin-row-actions">
          <PrimaryAction
            disabled={busy || label.trim() === row.label || label.trim().length < 3}
            onClick={() => void run(() => apiPatch(`${base}/label`, { label }), 'Renamed.')}
          >
            Rename
          </PrimaryAction>
          {/* Renaming is display-only — the rule never reads the label — so it needs no confirmation. */}
          <span className="learn-note">Renaming changes nothing about who can see anything.</span>
        </div>
      </div>

      <div>
        {!editingRule ? (
          <QuietAction disabled={busy} onClick={() => setEditingRule(true)}>
            Change who it admits…
          </QuietAction>
        ) : (
          <div className="kombi-dialog-stack">
            <RuleFields
              kind={kind}
              value={value}
              departments={departments}
              appointmentRoles={appointmentRoles}
              onKind={setKind}
              onValue={setValue}
              idPrefix={`ar-${row.accessRequirementId}`}
            />
            {carriesTools && (
              <div className="kombi-admin-card">
                <label htmlFor={`ar-${row.accessRequirementId}-ack`} className="kombi-admin-inline">
                  <input
                    id={`ar-${row.accessRequirementId}-ack`}
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />{' '}
                  <span>
                    I understand this changes who can see{' '}
                    <strong>
                      {row.toolCount} {row.toolCount === 1 ? 'tool' : 'tools'}
                    </strong>{' '}
                    straight away. Nothing on those tools will record it, and colleagues who lose
                    access see them disappear without an explanation.
                  </span>
                </label>
              </div>
            )}
            <div className="kombi-admin-row-actions">
              <PrimaryAction
                disabled={
                  busy || (kind !== 'all' && value === '') || (carriesTools && !acknowledged)
                }
                onClick={() =>
                  void run(
                    () =>
                      apiPatch(`${base}/rule`, {
                        ruleKind: kind,
                        ...(kind === 'all' ? { ruleValue: null } : { ruleValue: value }),
                        ...(carriesTools ? { consequenceAcknowledged: acknowledged } : {}),
                      }),
                    'Rule changed.',
                  )
                }
              >
                Save rule
              </PrimaryAction>
              <QuietAction disabled={busy} onClick={() => setEditingRule(false)}>
                Cancel
              </QuietAction>
            </div>
          </div>
        )}
      </div>

      <div className="kombi-admin-row-actions">
        {row.retiredAt === null ? (
          <QuietAction
            danger
            disabled={busy || row.isDefault}
            onClick={() => void run(() => apiPost(`${base}/retire`), 'Retired.')}
            title={
              row.isDefault
                ? 'The default cannot be retired — make another value the default first.'
                : 'Removes it from the upload form. Tools already using it keep it and stay as visible as they are now.'
            }
          >
            Retire (keeps existing tools)
          </QuietAction>
        ) : (
          <QuietAction
            disabled={busy}
            onClick={() => void run(() => apiPost(`${base}/reinstate`), 'Reinstated.')}
          >
            Reinstate
          </QuietAction>
        )}
        <QuietAction
          disabled={
            busy ||
            row.isDefault ||
            row.retiredAt !== null ||
            row.ruleKind === 'department' ||
            row.ruleKind === 'appointment'
          }
          onClick={() => void run(() => apiPost(`${base}/default`), 'Default moved.')}
          title={
            row.ruleKind === 'department' || row.ruleKind === 'appointment'
              ? 'A restricting value must not be the default — every future upload would be silently limited.'
              : 'What a builder gets when they express no preference.'
          }
        >
          Make default
        </QuietAction>
      </div>
      {message &&
        (isRefusal(message) ? (
          <ErrorLine>{message}</ErrorLine>
        ) : (
          <p className="learn-note">{message}</p>
        ))}
    </li>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData →
 * accessRequirementsLoader), shows the pending line while loading, an ErrorLine for a failed load / ended
 * session, NotPermitted for a forbidden result, and the create form + the list on success —
 * every mutation reload()s in place rather than revalidating the router.
 */
export function AccessRequirementsPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(accessRequirementsLoader);

  return (
    <KombiAdminOverlay title="Access Requirements" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading access requirements…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the access list could not be loaded. Close this panel and sign
          in again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Access Requirements" />
      ) : data && data.state === 'ok' ? (
        (() => {
          const { requirements, departments, appointmentRoles } = data.payload;
          return (
            <div>
              <ProvisionalNote />
              <Notice>
                Who may see a shared tool (CAT-F14). A builder chooses one of these values when they
                upload; the catalogue then hides a tool from anyone its value does not admit, on
                every route. Retiring a value takes it off the upload form and leaves every tool
                already using it exactly as visible as it is now — nothing here deletes anything.
              </Notice>

              <SectionHeader>Add a value</SectionHeader>
              <CreateForm
                departments={departments}
                appointmentRoles={appointmentRoles}
                onSaved={reload}
              />

              <SectionHeader>The list</SectionHeader>
              <ul className="kombi-dialog-stack">
                {requirements.map((row) => (
                  <RequirementRow
                    key={row.accessRequirementId}
                    row={row}
                    departments={departments}
                    appointmentRoles={appointmentRoles}
                    onSaved={reload}
                  />
                ))}
              </ul>
            </div>
          );
        })()
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/access-requirements deep link. Opens the panel
 * always-open over a blank backdrop; the panel still fetches via useOverlayData, and Close returns to
 * /admin.
 */
export function AccessRequirements() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <AccessRequirementsPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
